import eachMinuteOfInterval from 'date-fns/eachMinuteOfInterval';
import endOfDay from 'date-fns/endOfDay';
import format from 'date-fns/format';
import minutesToHours from 'date-fns/minutesToHours';
import startOfDay from 'date-fns/startOfDay';

type GenerateHoursByIntervalsParams = {
  intervalSize?: number;
  customFormat?: string;
  firstHour?: number;
  lastHour?: number;
};

export function generateHoursByIntervals({
  intervalSize = 15,
  customFormat = 'HH:mm',
  firstHour,
  lastHour
}: GenerateHoursByIntervalsParams): string[] {
  const date = new Date();
  const hasHours = firstHour != null && lastHour != null;

  let start = startOfDay(date);
  let end = endOfDay(date);

  if (hasHours) {
    validateHours(firstHour, lastHour);

    start.setHours(firstHour, 0, 0, 0);
    end.setHours(lastHour, 59, 0, 0);
  }

  const hoursByInterval = eachMinuteOfInterval(
    {
      start,
      end
    },
    {
      step: intervalSize
    }
  );

  return hoursByInterval.map(hourByInterval =>
    format(hourByInterval, customFormat)
  );
}

function validateHours(firstHour: number, lastHour: number) {
  const isFirstHourGreaterThanLast = firstHour > lastHour;
  const isHourOutOfRange = (hour: number) => hour < 0 || hour > 23;

  if (
    isFirstHourGreaterThanLast ||
    isHourOutOfRange(firstHour) ||
    isHourOutOfRange(lastHour)
  ) {
    throw new Error('Hours provided are invalid');
  }
}

export const COMMON_DATETIME_FORMATS = {
  YMMdd: (date: Date) => format(date, 'y-MM-dd'),
  YMM01: (date: Date) => format(date, 'y-MM-01'),
  dayDateOnly: (date: Date) => format(date, 'EEEE, LLL d'),
  dayDateAndTime: (date: Date) => format(date, "EEEE, LLL d 'at' h:mm a"),
  dayTruncated: (date: Date) => format(date, 'EEE, LLL d'),
  // 12 hr time
  hmm_aa: (date: Date) => format(date, 'h:mm aa'),
  iso: (date: Date) => format(date, "yyyy-MM-dd'T'HH:mm:ss")
};

export const getTimeFrameLabel = (differenceInUnit: number, unit: string) =>
  `${differenceInUnit} ${unit}${differenceInUnit === 1 ? '' : 's'}`;

export const MINUTES_IN_A_DAY = 60 * 24;

export const getCancellationWindow = (cancellationDeadline: number) => {
  const conversionUtil =
    cancellationDeadline < MINUTES_IN_A_DAY
      ? getWindowInHours
      : getWindowInDays;
  return conversionUtil(cancellationDeadline);
};

const minutesToDays = (minutes: number) => minutes / MINUTES_IN_A_DAY;

const getWindowInDays = (minutes: number) =>
  getTimeFrameLabel(minutesToDays(minutes), 'day');

const getWindowInHours = (minutes: number) =>
  getTimeFrameLabel(minutesToHours(minutes), 'hour');
